import { CSSProperties } from 'react';
import styled from 'styled-components';
import {
  ComponentMatrixListValueHeaderCellContainer,
  HeaderCell,
  ListValueLabel,
} from './atoms';
import {
  ComponentMatrixDimension,
  ComponentMatrixGroupOwnerType,
} from '../../types';
import { ComponentMatrix2Commands, GroupWithGridInfo } from '../types';
import { hasChildren } from '../../util';
import { COMPONENT_HIERARCHY_PADDING, NONE_HEADER_LABEL } from '../../consts';
import { ColumnHeaderComponent } from './columnHeaderComponent';
import { truncationPopoverAttrs } from './truncationPopover';
import { getColumnHeaderCardStyle } from './getRowHeaderCellStyle';
import { ArdoqId } from '@ardoq/api-types';
import {
  BODY_CELL_CONTAINER_HORIZONTAL_MARGIN,
  CARD_COLUMN_GROUP_BOTTOM_MARGIN,
  CARD_COLUMN_GROUP_RIGHT_MARGIN,
  CARD_COLUMN_GROUP_TOP_MARGIN,
  DEFAULT_REPRESENTATION_DATA,
} from '../consts';

/**
 * Absolutely-positioned overlay that draws the group container outline below
 * the top stripe. `top` is set via inline style so it tracks the per-level
 * stripe height (which grows when labels wrap to multiple lines).
 */
const GroupOutline = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

type ComponentMatrixHeaderCellArgs = {
  column: GroupWithGridInfo;
  columnDepth: number;
  gridColumn: number;
  colSpan: number;
  isFirstSibling: boolean;
  isLastSibling: boolean;
  stripeHeight: number;
  hoveredComponentId: ArdoqId | null;
  focusedComponentId: ArdoqId | null;
  contextComponentId: ArdoqId | null;
  commands: ComponentMatrix2Commands;
};

const ComponentMatrixHeaderCell = ({
  column,
  columnDepth,
  gridColumn,
  colSpan,
  isFirstSibling,
  isLastSibling,
  stripeHeight,
  hoveredComponentId,
  focusedComponentId,
  contextComponentId,
  commands,
}: ComponentMatrixHeaderCellArgs) => {
  const {
    isHidden,
    ownerType,
    label,
    address,
    isExpanded,
    fillColor,
    borderColor,
    isDark,
  } = column;
  if (isHidden) {
    return null;
  }

  const isReference =
    ownerType === ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT;
  const isPadding =
    label === COMPONENT_HIERARCHY_PADDING ||
    ownerType === ComponentMatrixGroupOwnerType.EMPTY;
  if (isPadding) {
    return null;
  }
  const level = address.length - 1;
  const rowLevel = columnDepth - level;
  const rowSpan = rowLevel;
  const rootLevel = level === 0;

  const { stripeStyle, spanStyle } = getColumnHeaderCardStyle(
    column,
    fillColor,
    borderColor
  );

  const showGroupOutline = isReference && isExpanded && hasChildren(column);

  // Horizontal margins are asymmetric on purpose. The LEFT edge is flush (4px,
  // matching the body cell) so a header lines up with its body column — left-side
  // nesting is already conveyed by the child occupying a later column track, so
  // no extra indent is needed (ARD-32761). The RIGHT edge keeps the cumulative
  // per-level margin: a parent and its last child share the same rightmost track,
  // so the child must inset from the parent's right border to preserve the
  // group-frame clearance (ARD-31376). No marginBottom: bottom spacing is encoded
  // in the last row's grid track height (see getColumnHeaderLevelHeights).
  const margins: CSSProperties = rootLevel
    ? {
        marginLeft: BODY_CELL_CONTAINER_HORIZONTAL_MARGIN,
        marginRight: BODY_CELL_CONTAINER_HORIZONTAL_MARGIN,
      }
    : isReference
      ? {
          marginTop: CARD_COLUMN_GROUP_TOP_MARGIN,
          marginLeft: BODY_CELL_CONTAINER_HORIZONTAL_MARGIN,
          marginRight: isLastSibling
            ? BODY_CELL_CONTAINER_HORIZONTAL_MARGIN +
              level * CARD_COLUMN_GROUP_RIGHT_MARGIN
            : 0,
        }
      : {
          marginLeft: isFirstSibling
            ? BODY_CELL_CONTAINER_HORIZONTAL_MARGIN
            : 0,
          marginRight: isLastSibling
            ? BODY_CELL_CONTAINER_HORIZONTAL_MARGIN
            : 0,
        };

  // Each nesting level pushes the GroupOutline bottom up by one CARD_COLUMN_GROUP_BOTTOM_MARGIN
  // so the parent outline's bottom border ends 8px below the child's (Figma spec: 8px padding).
  // With height:auto + align-self:stretch the content-box bottom equals the grid area bottom
  // (no marginBottom overflow), so the formula is simply level * 8px.
  const groupOutlineBottom = level * CARD_COLUMN_GROUP_BOTTOM_MARGIN;

  const headerCellStyle: CSSProperties = {
    gridColumn: `${gridColumn} / ${gridColumn + colSpan}`,
    gridRow: `${level + 1} / ${level + 1 + rowSpan}`,
    // height:auto + align-self:stretch (grid default): margin-box fills the grid area,
    // content-box bottom aligns with the grid area bottom. Overrides the styled
    // component's height:100% which would cause the content-box to overflow by marginTop.
    height: 'auto',
    // position: relative on expanded parents enables the GroupOutline overlay;
    // on child cells it creates a stacking context so they paint above the
    // parent's GroupOutline (which would otherwise obscure them).
    position: 'relative',
    ...margins,
  };
  const modelId = address.at(-1) ?? null;
  return (
    <HeaderCell
      style={headerCellStyle}
      onMouseEnter={() =>
        commands.onItemMouseEnter(isReference ? modelId : null)
      }
      onMouseLeave={commands.onItemMouseLeave}
      onClick={e => {
        e.stopPropagation();
        commands.onItemSelect(column.label);
        if (isReference && modelId) {
          commands.onComponentClick(modelId);
        }
      }}
    >
      {isReference && column.label ? (
        <>
          {showGroupOutline && (
            <GroupOutline
              style={{
                ...spanStyle,
                top: stripeHeight,
                bottom: groupOutlineBottom,
              }}
            />
          )}
          <ColumnHeaderComponent
            representationData={
              column.representationData ?? DEFAULT_REPRESENTATION_DATA
            }
            group={column}
            showExpander={hasChildren(column) && !isPadding}
            isExpanded={isExpanded}
            expanderClicked={() =>
              commands.onGroupExpanderClick({
                dimension: ComponentMatrixDimension.COLUMN,
                address,
              })
            }
            labelDoubleClicked={() => commands.onComponentDoubleClick(modelId!)}
            isHighlighted={
              hoveredComponentId === modelId! || focusedComponentId === modelId!
            }
            isContext={contextComponentId === modelId}
            stripeStyle={stripeStyle}
            stripeHeight={stripeHeight}
            isDark={isDark}
            /* Fill the cell when this is a level-0 cell without a GroupOutline
             * — i.e. a collapsed or leaf top-level column. Such cells span the
             * full column-header area vertically, so the stripe should fill
             * (ARD-31376). Child cells (level > 0) reserve 8px below the
             * stripe for the parent GroupOutline's bottom-stagger, so they
             * must NOT fill — they'd otherwise touch the parent outline's
             * bottom edge. */
            fillCell={!showGroupOutline && rootLevel}
          />
        </>
      ) : (
        <ComponentMatrixListValueHeaderCellContainer
          $isMinHeight={column.isMinHeight}
        >
          <ListValueLabel {...truncationPopoverAttrs}>
            {label || NONE_HEADER_LABEL}
          </ListValueLabel>
        </ComponentMatrixListValueHeaderCellContainer>
      )}
    </HeaderCell>
  );
};
export { ComponentMatrixHeaderCell };
